/**
 * Timed "Repeats every" and Overdue minimums for MaintenanceTaskModal
 * (matrix A5 / RT18 / RC15, CD-resolved: minimum 10 minutes).
 * Shared with unit tests so the payload cannot drift from the modal.
 */

/** Matches InstrumentTaskIntervalType::MIN_OVERDUE_MINUTES (the compliance job interval). */
export const MIN_OVERDUE_MINUTES = 10

export interface TimedIntervalSchedule {
  intervalHours: number | ''
  frequencyRrule: string
}

/**
 * Whole-hour cadences keep the stored interval_hours (legacy parity). Sub-hour or fractional-hour
 * cadences (e.g. every 30 minutes) have no whole-hour value, so they are driven by a MINUTELY
 * rrule with interval_hours left blank instead of being rounded up to an hour.
 */
export const buildTimedIntervalSchedule = (repeatsEveryMinutes: number): TimedIntervalSchedule => {
  const minutes = Math.max(1, Math.round(repeatsEveryMinutes))
  if (minutes % 60 === 0) return { intervalHours: minutes / 60, frequencyRrule: '' }
  return { intervalHours: '', frequencyRrule: `FREQ=MINUTELY;INTERVAL=${minutes}` }
}

/** True when an entered Overdue window is below the 10-minute minimum. */
export const overdueBelowMinimum = (overdueMinutes: number): boolean =>
  overdueMinutes < MIN_OVERDUE_MINUTES

/** Legacy parity (frequencies-list.component): Overdue is capped at 100000 in the entered unit. */
export const MAX_OVERDUE_VALUE = 100000

/** instrument_task_interval_types.late_time_interval_hours is a 32-bit integer column (minutes). */
export const MAX_OVERDUE_MINUTES = 2147483647

/** True when the entered Overdue number is above the 100000 maximum (REBUILD-629). */
export const overdueAboveMaximum = (overdueValue: number): boolean =>
  overdueValue > MAX_OVERDUE_VALUE

/**
 * True when the Overdue converted to minutes cannot be stored, e.g. 100000 years. Non-finite
 * values (a pasted `1e999` parses to Infinity) count as too long.
 */
export const overdueDurationTooLong = (overdueMinutes: number): boolean =>
  !Number.isFinite(overdueMinutes) || overdueMinutes > MAX_OVERDUE_MINUTES
